import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, ArrowsClockwise, CheckCircle, WarningCircle, PlugsConnected,
} from "@phosphor-icons/react";

const EMPTY = {
  from_name: "", from_email: "", smtp_host: "", smtp_port: 465,
  smtp_user: "", smtp_pass: "", signature: "", use_tls: true,
};

export default function Postfaecher() {
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/mailboxes");
      setMailboxes(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditId(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (m) => {
    setEditId(m.id);
    setForm({
      from_name: m.from_name || "", from_email: m.from_email || "",
      smtp_host: m.smtp_host || "", smtp_port: m.smtp_port || 465,
      smtp_user: m.smtp_user || "", smtp_pass: "",
      signature: m.signature || "", use_tls: m.use_tls !== false,
    });
    setDialog(true);
  };

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const save = async () => {
    if (!form.from_email.trim() || !form.smtp_host.trim() || !form.smtp_user.trim())
      return toast.error("E-Mail, SMTP-Server und Benutzername sind erforderlich");
    if (!editId && !form.smtp_pass.trim())
      return toast.error("SMTP-Passwort erforderlich");
    setSaving(true);
    try {
      const payload = { ...form, smtp_port: Number(form.smtp_port) || 465 };
      if (editId && !payload.smtp_pass.trim()) delete payload.smtp_pass;
      if (editId) {
        const { data } = await api.patch(`/mailboxes/${editId}`, payload);
        setMailboxes((ms) => ms.map((m) => (m.id === editId ? data : m)));
        toast.success("Postfach aktualisiert");
      } else {
        const { data } = await api.post("/mailboxes", payload);
        setMailboxes((ms) => [...ms, data]);
        toast.success("Postfach angelegt");
      }
      setDialog(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const test = async (m) => {
    setTesting(m.id);
    try {
      const { data } = await api.post(`/mailboxes/${m.id}/test`);
      setMailboxes((ms) => ms.map((x) => (x.id === m.id ? { ...x, last_test: data } : x)));
      if (data.ok) toast.success("Verbindung & Anmeldung erfolgreich");
      else toast.error(`Verbindungstest fehlgeschlagen: ${data.error}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test fehlgeschlagen");
    } finally {
      setTesting(null);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Postfach ${m.from_email} löschen?`)) return;
    await api.delete(`/mailboxes/${m.id}`);
    setMailboxes((ms) => ms.filter((x) => x.id !== m.id));
    toast.success("Postfach gelöscht");
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Postfächer</h1>
          <p className="text-sm text-zinc-500 mt-1">
            SMTP-Postfächer mit Absendername und Signatur verwalten. Diese werden für den E-Mail-Versand genutzt.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-mailbox-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
          <Plus size={16} className="mr-2" /> Postfach hinzufügen
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 py-12 text-center">Lade…</div>
      ) : mailboxes.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-md p-12 text-center text-zinc-500" data-testid="mailboxes-empty">
          Noch kein Postfach eingerichtet. Lege dein erstes SMTP-Postfach an.
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto" data-testid="mailboxes-table">
          <table className="w-full text-sm border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 font-medium">Absender</th>
                <th className="px-4 py-3 font-medium">E-Mail</th>
                <th className="px-4 py-3 font-medium">SMTP-Server</th>
                <th className="px-4 py-3 font-medium">Port</th>
                <th className="px-4 py-3 font-medium">Benutzer</th>
                <th className="px-4 py-3 font-medium">Letzter Test</th>
                <th className="px-4 py-3 font-medium text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {mailboxes.map((m) => (
                <tr key={m.id} data-testid="mailbox-row" className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-medium">{m.from_name || <span className="text-zinc-300">—</span>}</td>
                  <td className="px-4 py-3 text-zinc-600">{m.from_email}</td>
                  <td className="px-4 py-3 text-zinc-600">{m.smtp_host}</td>
                  <td className="px-4 py-3 text-zinc-600">{m.smtp_port} {m.use_tls !== false ? "(SSL)" : "(STARTTLS)"}</td>
                  <td className="px-4 py-3 text-zinc-600">{m.smtp_user}</td>
                  <td className="px-4 py-3">
                    {m.last_test ? (
                      m.last_test.ok ? (
                        <span className="inline-flex items-center gap-1 text-xs text-[#00796B]" data-testid={`test-result-${m.id}`}>
                          <CheckCircle size={14} weight="fill" /> OK · {fmt(m.last_test.at)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-[#E53935]" title={m.last_test.error} data-testid={`test-result-${m.id}`}>
                          <WarningCircle size={14} weight="fill" /> Fehler
                        </span>
                      )
                    ) : (
                      <span className="text-xs text-zinc-400">Noch nicht getestet</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => test(m)} disabled={testing === m.id} data-testid={`test-mailbox-${m.id}`} className="h-8 text-xs">
                        {testing === m.id ? <ArrowsClockwise size={15} className="mr-1 animate-spin" /> : <PlugsConnected size={15} className="mr-1" />}
                        Testen
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-mailbox-${m.id}`} className="h-8 text-xs">
                        <PencilSimple size={15} className="mr-1" /> Bearbeiten
                      </Button>
                      <button onClick={() => remove(m)} data-testid={`delete-mailbox-${m.id}`} className="ml-1 text-zinc-300 hover:text-[#E53935]">
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl" data-testid="mailbox-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Postfach bearbeiten" : "Postfach hinzufügen"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-zinc-600">Absendername</Label>
              <Input value={form.from_name} onChange={(e) => set("from_name", e.target.value)} placeholder="Max Mustermann" className="mt-1 h-9" data-testid="mailbox-from-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-600">Absender-E-Mail *</Label>
              <Input type="email" value={form.from_email} onChange={(e) => set("from_email", e.target.value)} placeholder="max@firma.de" className="mt-1 h-9" data-testid="mailbox-from-email" />
            </div>
            <div>
              <Label className="text-xs text-zinc-600">SMTP-Server *</Label>
              <Input value={form.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtp.example.com" className="mt-1 h-9" data-testid="mailbox-smtp-host" />
            </div>
            <div>
              <Label className="text-xs text-zinc-600">Port *</Label>
              <div className="flex items-center gap-3 mt-1">
                <Input type="number" value={form.smtp_port} onChange={(e) => set("smtp_port", e.target.value)} className="h-9 w-28" data-testid="mailbox-smtp-port" />
                <div className="flex items-center gap-2">
                  <Switch checked={form.use_tls} onCheckedChange={(v) => set("use_tls", v)} data-testid="mailbox-use-tls" />
                  <span className="text-xs text-zinc-500">{form.use_tls ? "SSL/TLS (465)" : "STARTTLS (587)"}</span>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-600">SMTP-Benutzername *</Label>
              <Input value={form.smtp_user} onChange={(e) => set("smtp_user", e.target.value)} placeholder="m01234" className="mt-1 h-9" data-testid="mailbox-smtp-user" />
            </div>
            <div>
              <Label className="text-xs text-zinc-600">SMTP-Passwort {editId ? "" : "*"}</Label>
              <Input type="password" value={form.smtp_pass} onChange={(e) => set("smtp_pass", e.target.value)} placeholder={editId ? "Leer lassen = unverändert" : ""} className="mt-1 h-9" data-testid="mailbox-smtp-pass" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-600">Signatur (reiner Text, inkl. Grußformel)</Label>
              <Textarea value={form.signature} onChange={(e) => set("signature", e.target.value)} rows={5}
                placeholder={"Mit freundlichen Grüßen\nMax Mustermann\nGastro-Beratung GmbH\nTel: 040 …"}
                className="mt-1 text-sm" data-testid="mailbox-signature" />
              <p className="text-xs text-zinc-400 mt-1">Wird automatisch an jede versendete E-Mail angehängt.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={save} disabled={saving} data-testid="save-mailbox-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
              {saving && <ArrowsClockwise size={16} className="mr-2 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
